import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { BiddingIntradayOrder } from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import {
  Controller,
  type UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  FieldRoot,
  HStack,
  Input,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { formatJstDateTimeHm } from '~/models/dateTime'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  wrapNumberInputChange,
} from '~/models/numberInputConstraint'
import { IntradayPreparingOptionLabel } from '~/pages/IntradayBidding/components/IntradayPreparingOption'
import { IntradayStrategyScheduleTiming } from '~/pages/IntradayBidding/components/IntradayStrategyScheduleTiming'
import { decimalVolumeToMw } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import type { IntradayScheduleRowInput } from '~/pages/IntradayBidding/models/intradayOrderWrite'
import { INTRADAY_P8_AVAILABLE } from '~/pages/IntradayBidding/models/intradayP8Availability'
import type { ScheduleDay } from '~/pages/IntradayBidding/models/strategySchedule'
import { resolveScheduleAppliedAt } from '~/pages/IntradayBidding/models/strategySchedule'
import {
  type IntradayStrategyFormValues,
  MAX_INTRADAY_SCHEDULE_ROWS,
  nextIntradayScheduleRow,
  savedBidModeOf,
} from '~/pages/IntradayBidding/models/strategyScheduleForm'

type Props = {
  form: UseFormReturn<IntradayStrategyFormValues>
  /** 編集対象の札 (= 行追加の既定の量・価格の出所)。 */
  order: BiddingIntradayOrder
  /** 受渡日 (= カレンダー日 `Date`)。 */
  deliveryDate: Date
  /** 対象コマ (1〜48)。 */
  koma: number
  /**
   * 成行の札か。 戦略モードは価格の指定を前提とするため成行に非対応
   * (spec intraday-bidding.md AC-BIN-004d) なので、 戦略の選択肢を無効にし、 理由を注記する。
   * 戦略は P8 の機能で、 `INTRADAY_P8_AVAILABLE` が false の間は成行かどうかによらず無効になり、
   * 押せない理由は準備中の 1 つだけを示す (AC-BIN-133)。
   */
  isMarketOrder: boolean
}

type BidModeFieldProps = {
  selected: 'normal' | 'strategy' | 'iceberg'
} & (
  | { readOnly: true }
  | {
      readOnly?: false
      isMarketOrder: boolean
      onSelect: (next: 'normal' | 'strategy') => void
    }
)

/**
 * 入札モード (通常 / 戦略 / アイスバーグ) の選択肢。 編集できる札も表示専用の札も同じ形で出す (AC-BIN-007)。
 * 表示専用では選択肢をすべて非活性にする。
 */
const IntradayBidModeField = (props: BidModeFieldProps): React.ReactElement => {
  const { t } = useTranslation()
  const { selected } = props
  return (
    <HStack gap={3}>
      <Text color="Text.highEmphasis" fontWeight="bold">
        {t('page:biddingIntraday.strategy.modeGroupLabel')}
      </Text>
      <AccessibleSegmentedControl
        aria-label={t('page:biddingIntraday.strategy.modeGroupLabel')}
        name="intradayBidMode"
        onSelect={(next) => {
          // アイスバーグは form の入札方式 (通常 / 戦略) に載らない。 選択肢は準備中の表示だけ。
          if (props.readOnly === true || next === 'iceberg') return
          props.onSelect(next)
        }}
        options={[
          {
            label: t('page:biddingIntraday.form.bidModeNormal'),
            value: 'normal',
            disabled: props.readOnly === true,
          },
          // 戦略・アイスバーグは P8 の機能。 選択肢に出したまま非活性にし、 理由は名前側の
          // ツールチップで示す (AC-BIN-133)。 非活性の選択肢は focus を受けないので包みを Tab で止める。
          {
            label: (
              <IntradayPreparingOptionLabel>
                {t('page:biddingIntraday.form.bidModeStrategy')}
              </IntradayPreparingOptionLabel>
            ),
            value: 'strategy',
            disabled:
              props.readOnly === true ||
              props.isMarketOrder ||
              !INTRADAY_P8_AVAILABLE,
          },
          ...(INTRADAY_P8_AVAILABLE
            ? []
            : [
                {
                  label: (
                    <IntradayPreparingOptionLabel>
                      {t('page:biddingIntraday.form.bidModeIceberg')}
                    </IntradayPreparingOptionLabel>
                  ),
                  value: 'iceberg' as const,
                  disabled: true,
                },
              ]),
        ]}
        selected={selected}
      />
    </HStack>
  )
}

/**
 * 入札編集モーダルの 入札方式 (通常 / 戦略) の切替と、 戦略モードのスケジュール表
 * (適用日時 × 量 × 価格、 AC-BIN-005b)。
 *
 * 各段の適用日時は「前日 / 当日」 + 時刻で入力し (時刻のみでは日跨ぎの前日 / 当日が
 * 一意に決まらない)、 段の下に前日/当日バッジ + GC 相対表現を出す。 範囲外
 * (開場前 / GC 以降) は同じ位置にその旨を出し、 保存時は該当欄を invalid にして
 * 段番号付きの文言を上部 Alert に集約し保存を止める (検証は
 * `buildIntradayStrategyFormSchema`)。
 *
 * 価格は固定値のみ。 段ごとのフォーミュラ (AC-BIN-005) は backend に評価の仕組みが
 * 無いため未対応。
 *
 * 戦略・アイスバーグは P8 の機能 (AC-BIN-133)。 `INTRADAY_P8_AVAILABLE` が false の間は
 * 両方を選択肢に出したまま非活性にし (戦略を選べる間は、 成行の札だけ非活性)、 押せない理由を
 * 名前側のツールチップで示す。 アイスバーグは実装が無いので、 true の間は選択肢に出さない。
 */
export const IntradayStrategyScheduleEditor = ({
  form,
  order,
  deliveryDate,
  koma,
  isMarketOrder,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = form
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'schedule',
  })
  const bidMode = useWatch({ control, name: 'bidMode' })
  const rows = useWatch({ control, name: 'schedule' })

  const atMaxRows = rows.length >= MAX_INTRADAY_SCHEDULE_ROWS
  const addRow = (): void => {
    // DS のボタンの disabled は aria-disabled (click は発火する) なので、 上限は
    // ここでも止める。
    if (atMaxRows) return
    append(nextIntradayScheduleRow(rows, order, deliveryDate))
  }

  const dayOptions: Array<{ label: string; value: ScheduleDay }> = [
    { label: t('page:biddingIntraday.strategy.dayBefore'), value: 'dayBefore' },
    {
      label: t('page:biddingIntraday.strategy.deliveryDay'),
      value: 'deliveryDay',
    },
  ]

  return (
    <VStack align="stretch" gap={3}>
      <IntradayBidModeField
        isMarketOrder={isMarketOrder}
        onSelect={(next) => {
          setValue('bidMode', next, { shouldDirty: true })
          // 戦略に切り替えた時に段が 1 つも無ければ 1 段目を用意する
          // (= 空の表から始めさせない。 mock-app BidEditDialog.SetModeStrategy)。
          if (next === 'strategy' && rows.length === 0) addRow()
        }}
        selected={bidMode}
      />
      {isMarketOrder && INTRADAY_P8_AVAILABLE && (
        <Text color="Text.midEmphasis">
          {t('page:biddingIntraday.strategy.marketOrderNotAvailable')}
        </Text>
      )}

      {bidMode === 'strategy' && (
        <VStack align="stretch" gap={2}>
          <Alert
            description={t('page:biddingIntraday.strategy.noteBody')}
            status="info"
            title={t('page:biddingIntraday.strategy.noteTitle')}
          />
          <TableRoot fontSize="md" size="sm" variant="line">
            <TableHeader>
              <TableRow>
                <TableColumnHeader>
                  {t('page:biddingIntraday.strategy.columnApplicationTiming')}
                </TableColumnHeader>
                <TableColumnHeader textAlign="right">
                  {t('page:biddingIntraday.strategy.columnVolume')}
                </TableColumnHeader>
                <TableColumnHeader textAlign="right">
                  {t('page:biddingIntraday.strategy.columnPrice')}
                </TableColumnHeader>
                <TableColumnHeader width="48px" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => {
                const row: IntradayScheduleRowInput | undefined = rows[index]
                const rowErrors = errors.schedule?.[index]
                const volumeReg = register(`schedule.${index}.volumeMw`)
                const priceReg = register(`schedule.${index}.priceYenPerKwh`)
                const position = index + 1
                const removeLabel = t(
                  'page:biddingIntraday.strategy.removeRow',
                  {
                    index: position,
                  },
                )
                return (
                  <TableRow key={field.id}>
                    <TableCell verticalAlign="top">
                      <FieldRoot invalid={rowErrors?.timeHm !== undefined}>
                        <HStack gap={2}>
                          <Controller
                            control={control}
                            name={`schedule.${index}.day`}
                            render={({ field: f }) => (
                              <AccessibleSegmentedControl
                                aria-label={t(
                                  'page:biddingIntraday.strategy.dayAriaLabel',
                                  { index: position },
                                )}
                                name={`intradayScheduleDay-${field.id}`}
                                onSelect={f.onChange}
                                options={dayOptions}
                                selected={f.value}
                              />
                            )}
                          />
                          <Input
                            width="112px"
                            aria-label={t(
                              'page:biddingIntraday.strategy.timeAriaLabel',
                              { index: position },
                            )}
                            type="time"
                            {...register(`schedule.${index}.timeHm`)}
                          />
                        </HStack>
                        <Box marginTop={1}>
                          <IntradayStrategyScheduleTiming
                            appliedAt={
                              row === undefined
                                ? undefined
                                : resolveScheduleAppliedAt({
                                    day: row.day,
                                    timeHm: row.timeHm,
                                    deliveryDate,
                                  })
                            }
                            deliveryDate={deliveryDate}
                            koma={koma}
                          />
                        </Box>
                      </FieldRoot>
                    </TableCell>
                    <TableCell verticalAlign="top">
                      <FieldRoot invalid={rowErrors?.volumeMw !== undefined}>
                        <Input
                          textAlign="right"
                          aria-label={t(
                            'page:biddingIntraday.strategy.volumeAriaLabel',
                            { index: position },
                          )}
                          max={JEPX_VOLUME_CONSTRAINT.max}
                          min={JEPX_VOLUME_CONSTRAINT.min}
                          step={JEPX_VOLUME_CONSTRAINT.step}
                          type="number"
                          {...volumeReg}
                          onChange={wrapNumberInputChange(
                            volumeReg,
                            JEPX_VOLUME_CONSTRAINT,
                          )}
                        />
                      </FieldRoot>
                    </TableCell>
                    <TableCell verticalAlign="top">
                      <FieldRoot
                        invalid={rowErrors?.priceYenPerKwh !== undefined}
                      >
                        <Input
                          textAlign="right"
                          aria-label={t(
                            'page:biddingIntraday.strategy.priceAriaLabel',
                            { index: position },
                          )}
                          max={JEPX_PRICE_CONSTRAINT.max}
                          min={JEPX_PRICE_CONSTRAINT.min}
                          step={JEPX_PRICE_CONSTRAINT.step}
                          type="number"
                          {...priceReg}
                          onChange={wrapNumberInputChange(
                            priceReg,
                            JEPX_PRICE_CONSTRAINT,
                          )}
                        />
                      </FieldRoot>
                    </TableCell>
                    <TableCell verticalAlign="top">
                      <IconOnlyButton
                        label={removeLabel}
                        onClick={() => {
                          remove(index)
                        }}
                        size="md"
                        variant="ghost"
                      >
                        <FaXmarkRegular />
                      </IconOnlyButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </TableRoot>
          {errors.schedule?.message !== undefined && (
            <Text color="Text.negativeMid">{errors.schedule.message}</Text>
          )}
          <HStack gap={3}>
            <TertiaryButton disabled={atMaxRows} onClick={addRow} size="sm">
              <FaPlusRegular />
              {t('page:biddingIntraday.strategy.addRow')}
            </TertiaryButton>
            {/* 上限に達したら追加を操作不可にして、 その旨を常時表示する (AC-BIN-005b)。 */}
            {atMaxRows && (
              <Text color="Text.midEmphasis">
                {t('page:biddingIntraday.strategy.maxRowsReached', {
                  max: MAX_INTRADAY_SCHEDULE_ROWS,
                })}
              </Text>
            )}
          </HStack>
        </VStack>
      )}
    </VStack>
  )
}

type ReadOnlyProps = {
  order: BiddingIntradayOrder
  deliveryDate: Date
  koma: number
}

/**
 * 編集できない札 (下書き以外 / GC 後) の 入札モードの選択肢と戦略モードのスケジュールの
 * 表示専用版 (AC-BIN-007「下書き以外は表示専用で開く」)。 選択肢は保存済みの入札モードを
 * 選んだ状態で、 すべて非活性にする (AC-BIN-133)。 下に出す段の表と選択肢を食い違わせない。
 */
export const IntradayStrategyScheduleReadOnly = ({
  order,
  deliveryDate,
  koma,
}: ReadOnlyProps): React.ReactElement => {
  const { t } = useTranslation()
  const savedBidMode = savedBidModeOf(order)
  const isStrategy = savedBidMode === 'strategy'
  return (
    <VStack align="stretch" gap={2}>
      <IntradayBidModeField readOnly selected={savedBidMode} />
      {isStrategy && (
        <Alert
          description={t('page:biddingIntraday.strategy.noteBody')}
          status="info"
          title={t('page:biddingIntraday.strategy.noteTitle')}
        />
      )}
      {isStrategy && (
        <TableRoot fontSize="md" size="sm" variant="line">
          <TableHeader>
            <TableRow>
              <TableColumnHeader>
                {t('page:biddingIntraday.strategy.columnApplicationTiming')}
              </TableColumnHeader>
              <TableColumnHeader textAlign="right">
                {t('page:biddingIntraday.strategy.columnVolume')}
              </TableColumnHeader>
              <TableColumnHeader textAlign="right">
                {t('page:biddingIntraday.strategy.columnPrice')}
              </TableColumnHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {order.scheduleEntries.map((entry, index) => {
              const appliedAt =
                entry.appliedAt === undefined
                  ? undefined
                  : timestampDate(entry.appliedAt)
              return (
                // 段は id を持たず、 表示専用で並び替えも無いので index を key にする。
                <TableRow key={index}>
                  <TableCell>
                    <VStack align="flex-start" gap={1}>
                      <Box as="span">
                        {appliedAt === undefined
                          ? '—'
                          : formatJstDateTimeHm(appliedAt)}
                      </Box>
                      <IntradayStrategyScheduleTiming
                        appliedAt={appliedAt}
                        deliveryDate={deliveryDate}
                        koma={koma}
                      />
                    </VStack>
                  </TableCell>
                  <TableCell textAlign="right">
                    {decimalVolumeToMw(entry.volumeMw) ?? '—'}
                  </TableCell>
                  <TableCell textAlign="right">
                    {entry.priceYenPerKwh?.value ?? '—'}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </TableRoot>
      )}
    </VStack>
  )
}
